// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { BenchmarkSelector } from '@shared/advisory/aggregation/components/benchmark-selector';
import {
  DonutChartContainer,
  KpiBoxes,
  OptionsSelector,
  PeriodPicker,
  SectionContainer,
  Tabs,
} from '@shared/ui-kit';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { Loader } from '@shared/advisory/aggregation/components/loader';
import { useAllocation } from './use-allocation';
import { OverviewStocks, OverviewTemplate } from '@shared/ui-kit/features/overview';

export const Allocation = defineComponent({
  displayName: 'Allocation',
  useSetup: useAllocation,
  View: ({
    t,
    kpis,
    periodPicker,
    onChangeBenchmark,
    benchmark,
    status,
    hasHoldings,
    banksAllocation,
    currencyAllocation,
    assetClassAllocation,
    vehiclesAllocation,
    tabsController,
    assetAnalysis,
    tabsOptions,
    currency,
    institutionId,
    onRefresh,
    savedBenchmarks,
  }) => (
    <div className="mt-6 space-y-10">
      <Loader
        status={status}
        shouldDisplayEmptyState={!hasHoldings}
        institution={institutionId}
        onRefresh={onRefresh}
      >
        <div className="flex flex-col items-center justify-center gap-5 md:flex-row md:justify-start">
          <PeriodPicker
            onValueChange={periodPicker.onPeriodChange}
            availableOptions={['YTD', '6M', '1Y', '3Y', '5Y', 'MAX']}
            defaultValue={periodPicker.defaultHist}
          />

          <div className="flex-0 flex items-center justify-around gap-5 md:flex-1">
            <BenchmarkSelector
              onChange={onChangeBenchmark}
              value={benchmark.ticker}
              savedBenchmarks={savedBenchmarks}
            />

            <div className="w-full max-w-[200px] md:ml-auto">
              <OptionsSelector
                defaultText={t('common.overview', { ns: 'common' })}
                optionsAvailable={tabsOptions}
                onChangeValueSelected={tabsController.setTab}
                selectType="white"
                defaultValue={tabsController.tab}
                optionSelected={tabsController.tab}
                dataTestId="allocation-tabs"
              />
            </div>
          </div>
        </div>

        <Tabs.Root
          defaultValue={tabsController.tab}
          controller={tabsController}
          baseId="allocation-tabs"
          variant="underline"
        >
          <Tabs.Content value="overview" className="space-y-10">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {kpis.map((vals, idx) => (
                <KpiBoxes key={idx} {...vals} />
              ))}
            </div>

            <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
              <SectionContainer>
                <DonutChartContainer
                  name={t('glossary.bank-broker', { ns: 'glossary' })}
                  exposure={banksAllocation.labels}
                  weight={banksAllocation.values}
                  testId="bank-broker-chart"
                />
              </SectionContainer>

              <SectionContainer>
                <DonutChartContainer
                  name={t('common.currency', { ns: 'common' })}
                  exposure={currencyAllocation.labels}
                  weight={currencyAllocation.values}
                  testId="currency-chart"
                />
              </SectionContainer>

              <SectionContainer>
                <DonutChartContainer
                  name={t('glossary.asset-class', { ns: 'glossary' })}
                  exposure={assetClassAllocation.labels}
                  weight={assetClassAllocation.values}
                  testId="asset-class-chart"
                />
              </SectionContainer>

              <SectionContainer>
                <DonutChartContainer
                  name={t('glossary.vehicles', { ns: 'glossary' })}
                  exposure={vehiclesAllocation.labels}
                  weight={vehiclesAllocation.values}
                  testId="vehicles-chart"
                />
              </SectionContainer>
            </div>
          </Tabs.Content>

          <Tabs.Content value="fund">
            <OverviewTemplate analysis={assetAnalysis.fund!} />
          </Tabs.Content>

          <Tabs.Content value="etf">
            <OverviewTemplate analysis={assetAnalysis.etf!} />
          </Tabs.Content>

          <Tabs.Content value="stock">
            <OverviewStocks analysis={assetAnalysis.stock!} currency={currency} />
          </Tabs.Content>
        </Tabs.Root>
      </Loader>
    </div>
  ),
});
